import {
  type Active,
  type ClientRect,
  type CollisionDescriptor,
  type CollisionDetection,
  type DragOverEvent,
  type DroppableContainer,
  type Modifier,
  type Over,
} from '@dnd-kit/core';
import { TouchEvent } from 'react';

import { insertPositionIndicatorCSS } from './ColumnOrder.sty.js';
import type {
  Column,
  DragAndDropTransform,
  Table,
} from '../../hooks/useTable/types.js';

/**
 * Number of pixels above which a touch move event is considered a drag motion.
 */
const TOUCH_DRAG_MOTION_THRESHOLD_PX = 5;

/**
 * Determines if the touch interaction is a drag motion.
 *
 * @param touchMove - The current touch move event.
 * @param touchStart - The preceding touch start event.
 * @returns A boolean indicating whether the touch move event is considered a drag motion.
 * @internal
 */
export function isDragMotion(
  touchMove: TouchEvent<Element>,
  touchStart?: TouchEvent<Element>,
): boolean {
  const touchStartX = touchStart?.touches[0].clientX;
  const touchMoveX = touchMove.touches[0].clientX;
  return (
    touchStartX !== undefined &&
    Math.abs(touchMoveX - touchStartX) > TOUCH_DRAG_MOTION_THRESHOLD_PX
  );
}

/**
 * Updates the column order state based on the active and over columns.
 *
 * @param active - The actively dragged item.
 * @param over - The droppable item which the active item is currently dragged over.
 * @param table - The table instance.
 * @param virtualizationContainer - The table's virtualization container.
 * @internal
 */
export const handleDragEnd = ({
  active,
  over,
  table,
  virtualizationContainer,
}: {
  active: Active;
  over: Over | null;
  table: Table<unknown>;
  virtualizationContainer?: HTMLDivElement;
}) => {
  if (
    over === null ||
    active.data.current === undefined ||
    over.data.current === undefined ||
    virtualizationContainer === undefined
  ) {
    return;
  }

  const activeColumn = active.data.current.column as Column<unknown, unknown>;
  const activeChildColumnIds = activeColumn.columns.map((child) => child.id);
  const activeChildColumnsCount = activeChildColumnIds.length;
  const activeParentColumnId = activeColumn.parent?.id;

  const overColumn = over.data.current.column as Column<unknown, unknown>;
  const overParentColumnId = over.data.current?.column?.parent?.id;
  const overChildColumnIds = overColumn.columns.map((child) => child.id);
  const overChildColumnsCount = overChildColumnIds.length;

  // move columns if both columns have the same parent or no parent
  if (activeParentColumnId === overParentColumnId) {
    const columnOrder = table.getState().columnOrder;
    const moveCount = Math.max(activeChildColumnsCount, 1);

    // get the active and over columns' indexes in the unmodified column order to consider hidden columns during reordering
    const activeColumnIndex = columnOrder.findIndex((columnId) => {
      const id =
        activeChildColumnsCount === 0
          ? activeColumn.id
          : // get the index of the first child column if the active column is a parent
            activeColumn.getLeafColumns()[0].id;

      return columnId === id;
    });
    const overColumnIndex = columnOrder.findIndex((columnId) => {
      const id =
        overChildColumnsCount === 0
          ? overColumn.id
          : // get the index of the first child column if the active column is a parent
            overColumn.getLeafColumns()[0].id;
      return columnId === id;
    });

    // update column order
    const newColumnOrder = [...columnOrder];
    const movedColumns = newColumnOrder.splice(activeColumnIndex, moveCount);
    // if moving more than one column to the right, toIndex needs to be adjusted since more than one column were spliced from the array
    const toIndex =
      overColumnIndex > activeColumnIndex
        ? overColumnIndex - (moveCount - 1)
        : overColumnIndex;

    newColumnOrder.splice(toIndex, 0, ...movedColumns);
    table.setColumnOrder(newColumnOrder);
  }

  table.setDraggedHeaderColumn(undefined);
  removeInsertPositionIndicator(lastStyledCells, virtualizationContainer, true);
};

// #region PositionIndicator
// save the cells that last received the indicator styles to be able to remove them again
const lastStyledCells = new Set<HTMLElement>();

/**
 * Transforms columns left/right to make space for the column which is currently being dragged ("active" column).
 * @param event - contains "active" (column being dragged) and "over" columns (column where the active column is currently dragged over).
 * @param virtualizationContainer - The table's virtualization container.
 * @internal
 */
export function indicateInsertPosition(
  event: DragOverEvent,
  virtualizationContainer?: HTMLDivElement,
): void {
  if (virtualizationContainer === undefined || !event.active || !event.over) {
    return;
  }

  const activeColumnIndex = event.active.data.current?.columnIndex;
  const overColumnId = event.over.id as string;
  const overColumnIndex = event.over.data.current?.columnIndex;

  // If the over column is a parent, we also need to render the indicator for the first/last child
  // depending on whether the active column is moved left or right
  const overColumnChildren = (
    event.over.data.current?.column as Column<unknown, unknown>
  ).getVisibleLeafColumns();
  const overFirstChildId = overColumnChildren.at(0)?.id;
  const overLastChildId = overColumnChildren.at(-1)?.id;

  if (activeColumnIndex < overColumnIndex) {
    // move right
    const overChildColumnId =
      overLastChildId !== overColumnId ? overLastChildId : undefined;
    renderInsertPositionIndicator(
      virtualizationContainer,
      overColumnId,
      overChildColumnId,
      'right',
    );
  } else if (activeColumnIndex > overColumnIndex) {
    // move left
    const overChildColumnId =
      overFirstChildId !== overColumnId ? overFirstChildId : undefined;
    renderInsertPositionIndicator(
      virtualizationContainer,
      overColumnId,
      overChildColumnId,
      'left',
    );
  } else {
    removeInsertPositionIndicator(lastStyledCells);
  }
}

/**
 * Renders an indicator at the position where the column will be inserted.
 */
function renderInsertPositionIndicator(
  virtualizationContainer: HTMLDivElement,
  columnId: string,
  childColumnId: string | undefined,
  position: 'left' | 'right',
) {
  const overCell = getHeaderCell(virtualizationContainer, columnId);
  const overChildColumnCell = getHeaderCell(
    virtualizationContainer,
    childColumnId,
  );
  if (overCell === null) {
    return;
  }

  // remove previous styles
  removeInsertPositionIndicator(lastStyledCells);
  // hide resize handles so they don't overlap the indicators
  const resizeHandles = getResizeHandles(virtualizationContainer);
  for (const resizeHandle of resizeHandles) {
    resizeHandle.style.display = 'none';
  }

  // add indicator after element
  addPositionIndicatorStyle(overCell, position);
  lastStyledCells.add(overCell);

  // add indicator also to child column if there is one
  if (overChildColumnCell) {
    addPositionIndicatorStyle(overChildColumnCell, position);
    lastStyledCells.add(overChildColumnCell);
  }
}

function addPositionIndicatorStyle(
  elem: HTMLElement,
  position: 'left' | 'right',
) {
  elem.classList.add(
    insertPositionIndicatorCSS.classNames.base,
    insertPositionIndicatorCSS.classNames.variants.left[
      position === 'left' ? 'true' : 'false'
    ],
  );
}

/**
 * Removes the insert position indicator from the specified cells.
 */
function removeInsertPositionIndicator(
  cells: Set<HTMLElement>,
  virtualizationContainer: HTMLDivElement | undefined = undefined,
  displayResizeHandles: boolean = false,
) {
  if (cells.size <= 0) {
    return;
  }

  if (displayResizeHandles && virtualizationContainer) {
    const resizeHandles = getResizeHandles(virtualizationContainer);
    for (const resizeHandle of resizeHandles) {
      resizeHandle.style.removeProperty('display');
    }
  }

  for (const cell of cells) {
    cell.classList.remove(
      insertPositionIndicatorCSS.classNames.base,
      insertPositionIndicatorCSS.classNames.variants.left.true,
      insertPositionIndicatorCSS.classNames.variants.left.false,
    );
    lastStyledCells.delete(cell);
  }
}

/**
 * Gets all cells with the given column id.
 * @param virtualizationContainer - The table's virtualization container.
 * @param columnId - id of the column (matches against `data-columnid`).
 * @returns matching cells.
 * @internal
 */
export function getHeaderCell(
  virtualizationContainer: HTMLDivElement,
  columnId?: string,
): HTMLElement | null {
  if (columnId === undefined) {
    return null;
  }
  return virtualizationContainer.querySelector<HTMLElement>(
    `[role='columnheader'][data-columnid="${CSS.escape(columnId)}"]`,
  );
}

/**
 * Gets all resize handles in the table.
 * @param virtualizationContainer - The table's virtualization container.
 * @returns matching elements.
 * @internal
 */
export function getResizeHandles(
  virtualizationContainer: HTMLDivElement,
): HTMLElement[] {
  const resizeHandles = virtualizationContainer.querySelectorAll<HTMLElement>(
    '[data-trigger="resize"]',
  );
  return Array.from(resizeHandles);
}
// #endregion PositionIndicator

// #region CollisionDetection
/**
 * Check if a given point is contained within a bounding rectangle
 */
function isPointWithinRectX(pointX: number, rect: ClientRect): boolean {
  return rect.left <= pointX && pointX <= rect.right;
}

/**
 * Custom collision detection algorithm for column order drag and drop.
 * Determines the closest droppable target within the X-axis based on the dragging direction.
 *
 * @param param0 - Object containing the droppable containers, the droppable rects, the collision rect and
 *                 the active element, which are needed for the collision detection.
 * @returns An array of collision descriptors, sorted by the distance to the dragged column, or an empty array if none is found.
 * @internal
 */
export const closestTargetWithinX: CollisionDetection = ({
  droppableContainers,
  droppableRects,
  active,
  collisionRect,
}) => {
  const initialRect = active.rect.current.initial;

  if (initialRect === null) {
    return [];
  }

  const collisions: CollisionDescriptor[] = [];

  // To make this work for varying column widths, depending on the dragging direction
  // we take either the left or the right of the dragged column to check for collisions
  // (minus a small offset, so the columns have to overlap a bit, before they would be swapped)
  const isDraggingLeft = collisionRect.left < initialRect.left;
  // min column width is 30px, to ensure the offset is in the left/right half: 30 / 2 - 1 = 14
  const COLLISION_OFFSET = 14;
  const collisionRectX = isDraggingLeft
    ? collisionRect.left + COLLISION_OFFSET
    : collisionRect.right - COLLISION_OFFSET;

  for (const droppableContainer of droppableContainers) {
    const { id } = droppableContainer;
    const rect = droppableRects.get(id);

    if (rect === undefined) {
      continue;
    }

    if (isPointWithinRectX(collisionRectX, rect)) {
      // return the container that the active rect is dragged over
      return [
        {
          id,
          data: { droppableContainer },
        },
      ];
    }

    // in case the dragged column's collisionRectX is not directly within any of the droppable containers
    // (e.g. when dragging over the scrollbar) we need to find the closest one. Therefore, we calculate
    // the distance between the droppable container's top/bottom and the collisionRectX coordinate.
    const droppableContainerX = isDraggingLeft ? rect.left : rect.right;
    const distance = Math.abs(collisionRectX - droppableContainerX);
    collisions.push({ id, data: { droppableContainer, value: distance } });
  }

  return collisions.sort((a, b) => a.data.value - b.data.value);
};

/**
 * Custom collision detection function that filters droppable containers based on the type of column being dragged,
 * and then applies the custom closestCenterWithinX algorithm on the filtered targets.
 *
 * @param active - The actively dragged item.
 * @param droppableContainers - The list of all droppable containers.
 * @internal
 */
export const customCollisionDetection: CollisionDetection = ({
  active,
  droppableContainers,
  ...rest
}) => {
  if (active.data.current === undefined) {
    return [];
  }

  const activeParentColumnId = active.data.current?.column?.parent?.id;
  let validTargets: DroppableContainer[] = [];

  // Filter droppable containers based on the type of column being dragged
  if (activeParentColumnId === undefined) {
    // Filter out child columns
    validTargets = droppableContainers.filter((target) => {
      const targetIsChildColumn =
        target.data.current?.column?.parent !== undefined;
      return targetIsChildColumn === false;
    });
  } else {
    // Only consider sibling child columns
    validTargets = droppableContainers.filter((target) => {
      const targetParentColumnId = target.data.current?.column?.parent?.id;
      return targetParentColumnId === activeParentColumnId; // Only siblings are valid
    });
  }

  // Filter builtin columns, as they are not valid targets
  validTargets = validTargets.filter(
    (target) => target.data.current?.column?.columnDef.meta?.builtin !== true,
  );

  // Apply closestCenterWithinX on filtered targets
  return closestTargetWithinX({
    active,
    droppableContainers: validTargets,
    ...rest,
  });
};
// #endregion CollisionDetection

// #region Modifiers
/**
 * Retrieves the left and right bounds of a column header.
 */
function getHeaderBounds(
  columnId: string,
  virtualizationContainer: HTMLDivElement,
): {
  left?: number;
  right?: number;
  height?: number;
} {
  const element = virtualizationContainer.querySelector<HTMLElement>(
    `[role="columnheader"][data-headerid="0"][data-columnId=${CSS.escape(
      columnId,
    )}]`,
  );
  if (element) {
    const { left, right, height } = element.getBoundingClientRect();
    return { left, right, height };
  }

  return {
    left: undefined,
    right: undefined,
    height: undefined,
  };
}

/**
 * Restricts the horizontal movement of an element within the specified bounds.
 *
 * @param transform - Transform values of the currently dragged element
 * @param rect - The bounding rectangle of the element being transformed.
 * @param tableBounds - The bounds within which the element should be restricted.
 * @returns The updated transform object with restricted x value.
 * @internal
 */
export function restrictToTableBounds(
  transform: DragAndDropTransform,
  rect: ClientRect,
  tableBounds: {
    left: number;
    right: number;
    top: number;
    bottom: number;
  },
): DragAndDropTransform {
  const value = {
    ...transform,
  };

  // horizontal restriction
  if (rect.left + transform.x <= tableBounds.left) {
    value.x = tableBounds.left - rect.left;
  } else if (rect.right + transform.x >= tableBounds.right) {
    value.x = tableBounds.right - rect.right;
  }
  // vertical restriction
  if (rect.top + transform.y <= tableBounds.top) {
    value.y = tableBounds.top - rect.top;
  } else if (rect.bottom + transform.y >= tableBounds.bottom) {
    value.y = tableBounds.bottom - rect.bottom;
  }

  return value;
}

/**
 * Creates a custom modifier to restrict drag and drop for column ordering.
 * This function ensures that columns cannot be dragged outside the table container,
 * and that child columns cannot be dragged past their parent.
 * If the dragging element would be moved past the desired bounds, the movement
 * coordinates will be manually adjusted accordingly.
 *
 * @param totalHeaderHeight - The height of the table header that is subtracted from the table height.
 * @param containedVariant - Whether the table has the `contained` variant set.
 * @param virtualizationContainer - The table's virtualization container.
 * @returns A custom dnd kit modifier.
 * @internal
 */
export const createRestrictColumnDragAndDrop = (
  totalHeaderHeight: number,
  containedVariant: boolean,
  virtualizationContainer?: HTMLDivElement,
): Modifier => {
  return ({ transform, active, draggingNodeRect }) => {
    if (!draggingNodeRect || virtualizationContainer === undefined) {
      return transform;
    }

    const activeColumnId = active?.data.current?.column?.id;
    const activeColumnParentId = active?.data.current?.column?.parent?.id;
    const hasChildColumns =
      activeColumnParentId === undefined &&
      active?.data.current?.column?.columns.length > 0;

    const parentRect = virtualizationContainer.getBoundingClientRect();
    const BORDER_WIDTH = 2;

    // Restrict dragging to the table container minus borders
    const tableBounds = {
      left: containedVariant ? parentRect.left + BORDER_WIDTH : parentRect.left,
      right: containedVariant
        ? parentRect.right - BORDER_WIDTH
        : parentRect.right,
      top: containedVariant ? parentRect.top + BORDER_WIDTH : parentRect.top,
      bottom: parentRect.bottom - totalHeaderHeight,
    };

    // If this is a parent or child column, the vertical/horizontal bounds need to be adjusted
    if (hasChildColumns || activeColumnParentId !== undefined) {
      const { left, right, height } = getHeaderBounds(
        hasChildColumns ? activeColumnId : activeColumnParentId,
        virtualizationContainer,
      );

      // If this is a child column, horizontal dragging should be restricted to the parent header
      if (
        activeColumnParentId !== undefined &&
        left !== undefined &&
        right !== undefined
      ) {
        tableBounds.left = left;
        tableBounds.right = right;
      }
      // If this is a parent or child column, vertical dragging should be restricted to the bottom of the table.
      // This check is necessary because the height of nested headers is smaller than the total header height
      if (height !== undefined) {
        // For parent columns add 1px to the height for the horizontal divider
        tableBounds.bottom += hasChildColumns ? height + 1 : height;
      }
    }

    return restrictToTableBounds(transform, draggingNodeRect, tableBounds);
  };
};
// #endregion Modifiers
